import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { IdentityService, type User } from '@/services/identity-service'
import type {
  DraftOrganizationDTO,
  DraftUserDTO,
  OrganizationDTO,
  UserDTO,
  UserRole,
} from '@01/identity-lib'
import { jwtDecode } from 'jwt-decode'
import { useStorage } from '@vueuse/core'

type AccessData = {
  token: string
  iss?: string
  sub?: string
  aud?: string[] | string
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
}
type LoggedInData = {
  user: User
  organization?: Organization
  access: AccessData
}

type Organization = {
  id: number
  slug_name: string
  display_name: string
}

const defaultOrg = {
  id: -1,
  slug_name: 'local_org',
  display_name: 'Local Org',
}
const defaultUser = {
  id: 1,
  org_id: 1,
  username: 'dev',
  full_name: 'dev',
  nick_name: 'dev',
  email: 'dev@localhost',
  phone: 'no-phone',
  roles: [],
  is_two_factor_enabled: false,
  is_passkey_enabled: false,
  created_at: new Date(),
  modified_at: new Date(),
} as User

const defaultAccess: AccessData = {
  token: '',
}

export const useIdentityStore = defineStore('identity-store', () => {
  const is_busy = ref(false)
  // JWT
  const default_data = {
    user: defaultUser,
    organization: defaultOrg,
    access: defaultAccess,
  }
  const auth = useStorage<LoggedInData>('auth-store', default_data)

  const is_only_for = (roles: UserRole[]) => {
    if (
      auth.value?.user &&
      auth.value.user.roles &&
      auth.value.user.roles.length > 0
    ) {
      return roles.some((role) => auth.value.user.roles.indexOf(role) > -1)
    }
    return false
  }

  const is_admin = computed(() => {
    return is_only_for(['super_admin', 'org_admin'])
  })

  function clear() {
    auth.value = undefined
  }

  function set_auth(data: LoggedInData) {
    const {
      access: { token = '' },
      user,
    } = data
    const decoded = jwtDecode(token)
    if (decoded) {
      const { exp, iat, aud, iss } = decoded
      const access = {
        token,
        iat,
        exp,
        aud,
        iss,
      }
      auth.value = { user, access }
    } else {
      throw new Error('Invalid token format')
    }
  }

  function set_organization(data: Organization) {
    auth.value.organization = data
  }

  function patch(data: LoggedInData) {
    auth.value.user = data.user
    auth.value.access = {
      ...auth.value.access,
      ...data.access,
    }
  }

  const is_authenticated = computed(() => {
    if (!auth.value.access?.token) {
      return false
    }
    const decoded: { exp: number } = jwtDecode(auth.value.access.token)
    const current_time = Math.floor(Date.now() / 1000)
    const is_valid = decoded.exp > current_time
    console.log('is_authenticated: ', is_valid)
    return is_valid
  })

  return {
    is_busy,
    // Role Guard
    is_only_for,
    is_admin,

    // JWT
    auth,
    set_auth,
    set_organization,
    patch,
    clear,
    is_authenticated,
  }
})
